import React, { FunctionComponent } from "react";
import NewsletterTitle from "components/v3/newsletter-management/title/NewsletterTitle";
import { Checkbox } from "bb.lego-ui";
import styles from "./styles.module.scss";

interface NewsletterLineItemProps extends InternalNewsletter {
    subscriberOnly?: boolean;
    userIsSubscribed?: boolean;
    userNewsletters: string[];
    nested?: boolean;
    onToggle?: (list: string, checked: boolean) => void;
    showCheckbox?: boolean;
}

function getAlreadySubscribedToThisNewsletter(userNewsletters: string[], list: string): boolean {
    return userNewsletters.some((newsletter) => newsletter === list);
}

const NewsletterLineItem: FunctionComponent<NewsletterLineItemProps> = (props) => {
    const {
        list,
        description,
        title,
        theme,
        sampleUrl,
        variants,
        subscriberOnly,
        userIsSubscribed,
        userNewsletters,
        nested,
        onToggle = (): void => undefined,
        showCheckbox = true
    } = props;

    if (variants) {
        return <NewsletterGroup { ...props } />;
    }

    if (!list) {
        return null;
    }

    const alreadySubscribedToThis = getAlreadySubscribedToThisNewsletter(userNewsletters, list);

    if (subscriberOnly && !userIsSubscribed && !alreadySubscribedToThis) {
        return null;
    }

    const className = `${ styles.item } ${ subscriberOnly ? styles.subscriberOnly : "" } ${ nested ? styles.nested : "" } ${ showCheckbox ? "" : styles.standalone }`;

    const newsletterTitle = <NewsletterTitle title={ title } style={ theme as string } nested={ nested } />;

    return (
        <div className={ className }>
            { showCheckbox ? (
                <Checkbox
                    size="large"
                    verticalAlignment="middle"
                    defaultChecked={ alreadySubscribedToThis }
                    onCheckboxChanged={ (checked: boolean): void => onToggle(list, checked) }
                >
                    { newsletterTitle }
                </Checkbox>
            ) : <div className={ styles.title }>{ newsletterTitle }</div> }
            { description && <p className={ styles.text }>{ description }</p> }
            { sampleUrl && (
                <div><a className={ styles.link } href={ sampleUrl } target="_blank">VIEW SAMPLE</a></div>
            ) }
        </div>
    );
};

const NewsletterGroup: FunctionComponent<NewsletterLineItemProps> = ({
    title,
    description,
    variants,
    subscriberOnly,
    userIsSubscribed,
    userNewsletters,
    onToggle
}) => {
    return (
        <div className={ styles.group }>
            <div className={ styles.title }><NewsletterTitle title={ title } /></div>
            { description && <p className={ styles.text }>{ description }</p> }
            <div>
                { (variants as NewsletterLineItemProps[]).map((newsletter) => {
                    if (!newsletter.list) {
                        return null;
                    }

                    const alreadySubscribedToThis = getAlreadySubscribedToThisNewsletter(userNewsletters, newsletter.list);
                
                    if (subscriberOnly && !userIsSubscribed && !alreadySubscribedToThis) {
                        return null;
                    }

                    return (
                        <NewsletterLineItem
                            { ...newsletter }
                            key={ newsletter.list }
                            userIsSubscribed={ userIsSubscribed }
                            userNewsletters={ userNewsletters }
                            nested={ true }
                            onToggle={ onToggle }
                        />
                    );
                }) }
                { subscriberOnly && !userIsSubscribed && (
                    <div><a className={ styles.link } href="/subscriptions?in_source=newsletter-management">Purchase a Bloomberg.com subscription to access</a></div>
                ) }
            </div>
        </div>
    );
};

export default NewsletterLineItem;
